import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  Calendar,
  Clock,
  CheckCircle,
  Filter,
  Download,
  RefreshCw,
  Shield,
  Target,
  TrendingUp,
  Eye,
} from "lucide-react";
import {
  PDFDownloadLink,
  Document,
  Page,
  Text,
  View,
  StyleSheet,
} from "@react-pdf/renderer";
import complianceRemindersService from "../../../api/services/complianceRemindersService";
import Button from "../../../components/common/Button/Button";

// PDF Styles
const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    backgroundColor: "#ffffff",
    padding: 20,
    fontFamily: "Helvetica",
  },
  header: {
    marginBottom: 20,
    textAlign: "center",
  },
  title: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#1f2937",
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 14,
    color: "#6b7280",
  },
  section: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#1f2937",
    marginBottom: 12,
    borderBottom: "2px solid #e5e7eb",
    paddingBottom: 8,
  },
  kpiRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
    paddingVertical: 4,
  },
  kpiLabel: {
    fontSize: 12,
    color: "#6b7280",
    flex: 1,
  },
  kpiValue: {
    fontSize: 12,
    fontWeight: "bold",
    color: "#1f2937",
    flex: 1,
    textAlign: "right",
  },
  table: {
    marginTop: 10,
  },
  tableRow: {
    flexDirection: "row",
    borderBottom: "1px solid #e5e7eb",
    paddingVertical: 8,
  },
  tableHeader: {
    backgroundColor: "#f9fafb",
    fontWeight: "bold",
  },
  tableHeaderCell: {
    fontSize: 10,
    fontWeight: "bold",
    color: "#374151",
    flex: 1,
    paddingHorizontal: 4,
  },
  tableCell: {
    fontSize: 10,
    color: "#6b7280",
    flex: 1,
    paddingHorizontal: 4,
  },
  reminderCard: {
    backgroundColor: "#f9fafb",
    padding: 12,
    marginBottom: 8,
    borderRadius: 4,
    border: "1px solid #e5e7eb",
  },
  reminderTitle: {
    fontSize: 12,
    fontWeight: "bold",
    color: "#1f2937",
    marginBottom: 4,
  },
  reminderDescription: {
    fontSize: 10,
    color: "#6b7280",
    marginBottom: 8,
  },
  reminderDetails: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 8,
  },
  reminderDetail: {
    flex: 1,
    marginRight: 8,
  },
  detailLabel: {
    fontSize: 9,
    color: "#6b7280",
    marginBottom: 2,
  },
  detailValue: {
    fontSize: 10,
    fontWeight: "bold",
    color: "#1f2937",
  },
  categoryCard: {
    backgroundColor: "#f9fafb",
    padding: 12,
    marginBottom: 8,
    borderRadius: 4,
    border: "1px solid #e5e7eb",
  },
  categoryTitle: {
    fontSize: 12,
    fontWeight: "bold",
    color: "#1f2937",
    marginBottom: 4,
  },
  categoryStats: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 8,
  },
  categoryStat: {
    flex: 1,
    marginRight: 8,
    textAlign: "center",
  },
  statLabel: {
    fontSize: 9,
    color: "#6b7280",
    marginBottom: 2,
  },
  statValue: {
    fontSize: 10,
    fontWeight: "bold",
    color: "#1f2937",
  },
  completedItem: {
    backgroundColor: "#f0fdf4",
    padding: 8,
    marginBottom: 6,
    borderRadius: 4,
    border: "1px solid #bbf7d0",
  },
  completedTitle: {
    fontSize: 11,
    fontWeight: "bold",
    color: "#166534",
    marginBottom: 2,
  },
  completedDetails: {
    fontSize: 9,
    color: "#6b7280",
  },
  footer: {
    position: "absolute",
    bottom: 20,
    left: 20,
    right: 20,
    textAlign: "center",
    fontSize: 10,
    color: "#6b7280",
  },
});

// PDF Component
const ComplianceRemindersPDF = ({
  complianceMetrics,
  upcomingReminders,
  completedItems,
  complianceCategories,
}) => {
  const getCurrentDate = () => {
    return new Date().toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high":
        return "#dc2626";
      case "medium":
        return "#d97706";
      case "low":
        return "#16a34a";
      default:
        return "#6b7280";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "#16a34a";
      case "in_progress":
        return "#0F2942";
      case "pending":
        return "#6b7280";
      case "overdue":
        return "#dc2626";
      default:
        return "#6b7280";
    }
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.title}>Compliance Reminders Report</Text>
          <Text style={styles.subtitle}>Generated on {getCurrentDate()}</Text>
        </View>

        {/* Key Metrics */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Key Metrics</Text>
          <View style={styles.kpiRow}>
            <Text style={styles.kpiLabel}>Overall Compliance</Text>
            <Text style={styles.kpiValue}>
              {complianceMetrics?.totalCompliance?.current || 0}%
            </Text>
          </View>
          <View style={styles.kpiRow}>
            <Text style={styles.kpiLabel}>Overdue Items</Text>
            <Text style={styles.kpiValue}>
              {complianceMetrics?.overdueItems?.current || 0}
            </Text>
          </View>
          <View style={styles.kpiRow}>
            <Text style={styles.kpiLabel}>Upcoming Deadlines</Text>
            <Text style={styles.kpiValue}>
              {complianceMetrics?.upcomingDeadlines?.current || 0}
            </Text>
          </View>
          <View style={styles.kpiRow}>
            <Text style={styles.kpiLabel}>Completed This Month</Text>
            <Text style={styles.kpiValue}>
              {complianceMetrics?.completedThisMonth?.current || 0}
            </Text>
          </View>
        </View>

        {/* Upcoming Compliance Items */}
        {upcomingReminders && upcomingReminders.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Upcoming Compliance Items</Text>
            {upcomingReminders.map((reminder, index) => (
              <View key={index} style={styles.reminderCard}>
                <Text style={styles.reminderTitle}>{reminder.title}</Text>
                <Text style={styles.reminderDescription}>
                  {reminder.description}
                </Text>
                <View style={styles.reminderDetails}>
                  <View style={styles.reminderDetail}>
                    <Text style={styles.detailLabel}>Due Date</Text>
                    <Text style={styles.detailValue}>{reminder.dueDate}</Text>
                  </View>
                  <View style={styles.reminderDetail}>
                    <Text style={styles.detailLabel}>Days Left</Text>
                    <Text
                      style={[
                        styles.detailValue,
                        { color: getPriorityColor(reminder.priority) },
                      ]}
                    >
                      {reminder.daysLeft} days
                    </Text>
                  </View>
                  <View style={styles.reminderDetail}>
                    <Text style={styles.detailLabel}>Priority</Text>
                    <Text
                      style={[
                        styles.detailValue,
                        { color: getPriorityColor(reminder.priority) },
                      ]}
                    >
                      {reminder.priority.toUpperCase()}
                    </Text>
                  </View>
                  <View style={styles.reminderDetail}>
                    <Text style={styles.detailLabel}>Assigned To</Text>
                    <Text style={styles.detailValue}>
                      {reminder.assignedTo}
                    </Text>
                  </View>
                </View>
              </View>
            ))}
          </View>
        )}

        {/* Compliance by Category */}
        {complianceCategories && complianceCategories.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Compliance by Category</Text>
            {complianceCategories.map((category, index) => (
              <View key={index} style={styles.categoryCard}>
                <Text style={styles.categoryTitle}>
                  {category.category} - {category.compliance}%
                </Text>
                <View style={styles.categoryStats}>
                  <View style={styles.categoryStat}>
                    <Text style={styles.statLabel}>Total</Text>
                    <Text style={styles.statValue}>{category.total}</Text>
                  </View>
                  <View style={styles.categoryStat}>
                    <Text style={styles.statLabel}>Completed</Text>
                    <Text style={[styles.statValue, { color: "#16a34a" }]}>
                      {category.completed}
                    </Text>
                  </View>
                  <View style={styles.categoryStat}>
                    <Text style={styles.statLabel}>Pending</Text>
                    <Text style={[styles.statValue, { color: "#d97706" }]}>
                      {category.pending}
                    </Text>
                  </View>
                  <View style={styles.categoryStat}>
                    <Text style={styles.statLabel}>Overdue</Text>
                    <Text style={[styles.statValue, { color: "#dc2626" }]}>
                      {category.overdue}
                    </Text>
                  </View>
                </View>
              </View>
            ))}
          </View>
        )}

        {/* Recently Completed */}
        {completedItems && completedItems.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Recently Completed</Text>
            {completedItems.map((item, index) => (
              <View key={index} style={styles.completedItem}>
                <Text style={styles.completedTitle}>{item.title}</Text>
                <Text style={styles.completedDetails}>
                  {item.category} • Completed by {item.completedBy} •{" "}
                  {item.completedDate}
                </Text>
                <Text style={styles.completedDetails}>
                  Time taken: {item.timeTaken}
                </Text>
              </View>
            ))}
          </View>
        )}

        {/* Footer */}
        <Text style={styles.footer}>
          Compliance Reminders Report - Generated on {getCurrentDate()}
        </Text>
      </Page>
    </Document>
  );
};

const ComplianceReminders = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedPriority, setSelectedPriority] = useState("all");
  const [viewType, setViewType] = useState("upcoming");
  const [selectedReminder, setSelectedReminder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [complianceMetrics, setComplianceMetrics] = useState(null);
  const [upcomingReminders, setUpcomingReminders] = useState([]);
  const [completedItems, setCompletedItems] = useState([]);
  const [complianceCategories, setComplianceCategories] = useState([]);
  // COMMENTED OUT: Risk assessment state
  // const [riskAssessment, setRiskAssessment] = useState([]);

  // Load compliance data from APIs
  useEffect(() => {
    loadComplianceData();
  }, [selectedCategory, selectedPriority, viewType]);

  const loadComplianceData = async () => {
    try {
      setLoading(true);
      // Load all compliance data in parallel
      const [
        metricsData,
        remindersData,
        completedData,
        categoriesData,
        // COMMENTED OUT: Risk assessment data
        // riskData,
      ] = await Promise.all([
        complianceRemindersService.getComplianceMetrics(),
        complianceRemindersService.getUpcomingReminders({
          category: selectedCategory !== "all" ? selectedCategory : undefined,
          priority: selectedPriority !== "all" ? selectedPriority : undefined,
        }),
        complianceRemindersService.getCompletedItems({
          category: selectedCategory !== "all" ? selectedCategory : undefined,
        }),
        complianceRemindersService.getComplianceCategories(),
        // COMMENTED OUT: Risk assessment service call
        // complianceRemindersService.getComplianceRiskAssessment(),
      ]);

      setComplianceMetrics(metricsData);
      setUpcomingReminders(remindersData);
      setCompletedItems(completedData);
      setComplianceCategories(categoriesData);
      // COMMENTED OUT: Set risk assessment data
      // setRiskAssessment(riskData);
    } catch (error) {
      // Error handling
    } finally {
      setLoading(false);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high":
        return "text-red-600 bg-red-50 border-red-200";
      case "medium":
        return "text-yellow-600 bg-yellow-50 border-yellow-200";
      case "low":
        return "text-green-600 bg-green-50 border-green-200";
      default:
        return "text-gray-600 bg-gray-50 border-gray-200";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "text-green-600 bg-green-50";
      case "in_progress":
        return "text-[#0F2942] bg-blue-50";
      case "pending":
        return "text-gray-600 bg-gray-50";
      case "overdue":
        return "text-red-600 bg-red-50";
      default:
        return "text-gray-600 bg-gray-50";
    }
  };

  const getRiskColor = (risk) => {
    switch (risk) {
      case "High":
        return "text-red-600 bg-red-50";
      case "Medium":
        return "text-yellow-600 bg-yellow-50";
      case "Low":
        return "text-green-600 bg-green-50";
      default:
        return "text-gray-600 bg-gray-50";
    }
  };

  const getComplianceStatusColor = (status) => {
    switch (status) {
      case "compliant":
        return "text-green-600 bg-green-50";
      case "needs_attention":
        return "text-yellow-600 bg-yellow-50";
      case "critical":
        return "text-red-600 bg-red-50";
      default:
        return "text-gray-600 bg-gray-50";
    }
  };

  const getDaysLeftColor = (days) => {
    if (days <= 7) return "text-red-600";
    if (days <= 14) return "text-yellow-600";
    return "text-green-600";
  };

  // Filter reminders based on selected filters
  const filteredReminders = upcomingReminders.filter((reminder) => {
    const categoryMatch =
      selectedCategory === "all" ||
      reminder.category.toLowerCase() === selectedCategory;
    const priorityMatch =
      selectedPriority === "all" || reminder.priority === selectedPriority;
    return categoryMatch && priorityMatch;
  });

  // Add source indicator for statutory compliance items
  const getSourceIndicator = (reminder) => {
    if (reminder.source === "statutory") {
      return (
        <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
          Statutory
        </span>
      );
    } else if (reminder.source === "task") {
      return (
        <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
          Task
        </span>
      );
    }
    return null;
  };

  // Handler functions
  const handleAddReminder = () => {
    navigate("/add_compliance_reminder");
  };

  const handleViewReminder = (reminder) => {
    navigate(`/compliance_reminder_detail/${reminder.id}`);
  };

  const handleEditReminder = (reminder) => {
    navigate(`/edit_compliance_reminder/${reminder.id}`);
  };

  const handleSetNotification = (reminder) => {
    navigate(`/reminder_notification_settings/${reminder.id}`);
  };

  // Show loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F2942] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading compliance data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">
            Compliance Reminders
          </h2>
          <p className="text-sm text-gray-600">
            Track regulatory deadlines and compliance requirements
          </p>
        </div>

        <div className="flex items-center space-x-4">
          <select
            value={viewType}
            onChange={(e) => setViewType(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            <option value="upcoming">Upcoming</option>
            <option value="completed">Completed</option>
            <option value="overdue">Overdue</option>
            <option value="all">All Items</option>
          </select>

          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            <option value="all">All Categories</option>
            <option value="pf">PF (Provident Fund)</option>
            <option value="esi">ESI</option>
            <option value="tds">TDS</option>
            <option value="pt">Professional Tax</option>
            <option value="lwf">Labour Welfare Fund</option>
            <option value="gratuity">Gratuity</option>
            <option value="bonus">Bonus</option>
            <option value="statutory">Statutory</option>
            <option value="tax">Tax</option>
            <option value="license">License</option>
            <option value="safety">Safety</option>
            <option value="audit">Audit</option>
          </select>

          <button
            onClick={loadComplianceData}
            className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Refresh</span>
          </button>

          <PDFDownloadLink
            document={
              <ComplianceRemindersPDF
                complianceMetrics={complianceMetrics}
                upcomingReminders={upcomingReminders}
                completedItems={completedItems}
                complianceCategories={complianceCategories}
              />
            }
            fileName={`Compliance_Reminders_Report_${new Date().toISOString().split("T")[0]
              }.pdf`}
          >
            {({ loading }) => (
              <Button
                variant="outline"
                disabled={loading}
                className="flex items-center space-x-2"
              >
                <Download className="h-4 w-4" />
                <span>Export</span>
              </Button>
            )}
          </PDFDownloadLink>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Overall Compliance
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {complianceMetrics?.totalCompliance?.current || 0}%
              </p>
            </div>
            <div className="p-3 bg-green-50 rounded-full">
              <Shield className="h-6 w-6 text-green-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center">
            <TrendingUp className="h-4 w-4 text-green-500" />
            <span className="text-sm text-green-600 ml-1">
              +
              {(
                complianceMetrics.totalCompliance.current -
                complianceMetrics.totalCompliance.previous
              ).toFixed(1)}
              %
            </span>
            <span className="text-sm text-gray-500 ml-2">
              Target: {complianceMetrics.totalCompliance.target}%
            </span>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Overdue Items</p>
              <p className="text-2xl font-bold text-gray-900">
                {complianceMetrics?.overdueItems?.current || 0}
              </p>
            </div>
            <div className="p-3 bg-red-50 rounded-full">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center">
            <TrendingUp className="h-4 w-4 text-green-500 transform rotate-180" />
            <span className="text-sm text-green-600 ml-1">
              -
              {complianceMetrics.overdueItems.previous -
                complianceMetrics.overdueItems.current}
            </span>
            <span className="text-sm text-gray-500 ml-2">improvement</span>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Upcoming Deadlines
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {complianceMetrics?.upcomingDeadlines?.current || 0}
              </p>
            </div>
            <div className="p-3 bg-yellow-50 rounded-full">
              <Calendar className="h-6 w-6 text-yellow-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center">
            <Clock className="h-4 w-4 text-yellow-500" />
            <span className="text-sm text-yellow-600 ml-1">Next 30 days</span>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Completed This Month
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {complianceMetrics?.completedThisMonth?.current || 0}
              </p>
            </div>
            <div className="p-3 bg-blue-50 rounded-full">
              <CheckCircle className="h-6 w-6 text-[#0F2942]" />
            </div>
          </div>
          <div className="mt-4 flex items-center">
            <TrendingUp className="h-4 w-4 text-green-500" />
            <span className="text-sm text-green-600 ml-1">
              +
              {complianceMetrics.completedThisMonth.current -
                complianceMetrics.completedThisMonth.previous}
            </span>
            <span className="text-sm text-gray-500 ml-2">vs last month</span>
          </div>
        </div>
      </div>

      {/* Upcoming Reminders */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">
            Upcoming Compliance Items
          </h3>
          <div className="flex items-center space-x-2">
            <select
              value={selectedPriority}
              onChange={(e) => setSelectedPriority(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="all">All Priorities</option>
              <option value="high">High Priority</option>
              <option value="medium">Medium Priority</option>
              <option value="low">Low Priority</option>
            </select>
            <button className="text-gray-400 hover:text-gray-600">
              <Filter className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="space-y-4">
          {filteredReminders.map((reminder) => (
            <div
              key={reminder.id}
              className={`border rounded-lg p-4 ${getPriorityColor(
                reminder.priority
              )}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h4 className="text-lg font-semibold text-gray-900">
                      {reminder.title}
                    </h4>
                    <span
                      className={`px-2 py-1 text-xs rounded-full ${getPriorityColor(
                        reminder.priority
                      )}`}
                    >
                      {reminder.priority.toUpperCase()}
                    </span>
                    <span
                      className={`px-2 py-1 text-xs rounded-full ${getStatusColor(
                        reminder.status
                      )}`}
                    >
                      {reminder.status.replace("_", " ").toUpperCase()}
                    </span>
                    {getSourceIndicator(reminder)}
                  </div>
                  <p className="text-sm text-gray-600 mb-3">
                    {reminder.description}
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Due Date</p>
                      <p className="font-medium text-gray-900">
                        {reminder.dueDate}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Days Left</p>
                      <p
                        className={`font-medium ${getDaysLeftColor(
                          reminder.daysLeft
                        )}`}
                      >
                        {reminder.daysLeft} days
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Assigned To</p>
                      <p className="font-medium text-gray-900">
                        {reminder.assignedTo}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2 ml-4">
                  <button
                    onClick={() => handleViewReminder(reminder)}
                    className="p-2 text-gray-400 hover:text-gray-600"
                    title="View Details"
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Compliance Categories & Risk Assessment */}
      <div className="grid grid-cols-1 gap-6">
        {/* Compliance by Category */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Compliance by Category
            </h3>
            <button className="text-gray-400 hover:text-gray-600">
              <Target className="h-5 w-5" />
            </button>
          </div>
          <div className="space-y-4">
            {complianceCategories?.map((category, index) => (
              <div key={index} className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium text-gray-900">
                    {category.category}
                  </h4>
                  <span
                    className={`px-2 py-1 text-xs rounded-full ${category.compliance >= 95
                        ? "text-green-600 bg-green-50"
                        : category.compliance >= 85
                          ? "text-yellow-600 bg-yellow-50"
                          : "text-red-600 bg-red-50"
                      }`}
                  >
                    {category.compliance}%
                  </span>
                </div>
                <div className="grid grid-cols-4 gap-2 text-xs">
                  <div className="text-center">
                    <p className="text-gray-500">Total</p>
                    <p className="font-semibold text-gray-900">
                      {category.total}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-gray-500">Completed</p>
                    <p className="font-semibold text-green-600">
                      {category.completed}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-gray-500">Pending</p>
                    <p className="font-semibold text-yellow-600">
                      {category.pending}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-gray-500">Overdue</p>
                    <p className="font-semibold text-red-600">
                      {category.overdue}
                    </p>
                  </div>
                </div>
                <div className="mt-2">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${category.compliance >= 95
                          ? "bg-green-500"
                          : category.compliance >= 85
                            ? "bg-yellow-500"
                            : "bg-red-500"
                        }`}
                      style={{ width: `${category.compliance}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recently Completed */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Recently Completed
          </h3>
          <button className="text-gray-400 hover:text-gray-600">
            <CheckCircle className="h-5 w-5" />
          </button>
        </div>
        <div className="space-y-3">
          {completedItems.map((item) => (
            <div
              key={item.id}
              className="flex items-center justify-between p-3 border rounded-lg bg-green-50"
            >
              <div className="flex items-center space-x-3">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {item.title}
                  </p>
                  <p className="text-xs text-gray-600">
                    {item.category} • Completed by {item.completedBy}
                    {item.source && (
                      <span
                        className={`ml-2 px-2 py-1 text-xs rounded-full ${item.source === "statutory"
                            ? "bg-blue-100 text-blue-800"
                            : "bg-green-100 text-green-800"
                          }`}
                      >
                        {item.source === "statutory" ? "Statutory" : "Task"}
                      </span>
                    )}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-900">{item.completedDate}</p>
                <p className="text-xs text-gray-600">
                  Time taken: {item.timeTaken}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ComplianceReminders;
